import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { firstValueFrom } from 'rxjs';
import { StaticRole, UserRole } from '@markant-platform/shared/user/model';
import { PlatformWidget, Widget } from '../../models/dashboard.models';
import { DashboardConfig } from '../configuration/dashboard-widgets.config';
import { OrganizationDetailData } from 'data-model';

@Component({
  selector: 'markant-platform-static-widgets-dashboard',
  templateUrl: './static-widgets-dashboard.component.html',
  styleUrls: ['./static-widgets-dashboard.component.scss'],
})
export class StaticWidgetsDashboardComponent implements OnInit, OnChanges {
  @Input() visibleWidgets: Widget[] = [];
  @Output() removeStaticWidget = new EventEmitter<string>();

  staticWidgets: PlatformWidget[] = [];
  allWidgets: PlatformWidget[] = [];

  constructor(
    private organizationService: OrganizationService,
    private authenticatedUserService: AuthenticatedUserService
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      const userRoles = await this.fetchUserRoles();
      const organization = await this.fetchOrganization();
      this.staticWidgets = this.filterStaticWidgets(userRoles, organization);
      this.updateAllWidgets();
    } catch (error) {
      console.error('Error during initialization:', error);
    }
  }

  ngOnChanges(): void {
    this.updateAllWidgets();
  }

  private async fetchUserRoles(): Promise<UserRole[]> {
    return firstValueFrom(this.authenticatedUserService.getUserRoles());
  }

  private async fetchOrganization(): Promise<OrganizationDetailData> {
    return firstValueFrom(this.organizationService.fetchOrganization());
  }

  private filterStaticWidgets(userRoles: UserRole[], organization: OrganizationDetailData): PlatformWidget[] {
    return DashboardConfig.PLATFORM_WIDGETS.STATIC.filter(widget => {
      const hasAllowedRole = this.checkAllowedRole(widget, userRoles);
      const hasAllowedQualificationLevel = this.checkAllowedQualificationLevel(widget, organization);
      const hasFurtherValidation = !widget.validationFunction || widget.validationFunction(organization);
      return hasAllowedRole && hasAllowedQualificationLevel && hasFurtherValidation;
    });
  }

  private checkAllowedRole(widget: PlatformWidget, userRoles: UserRole[]): boolean {
    return userRoles.some(({ name }) => widget.allowedRoles.includes(name as StaticRole));
  }

  private checkAllowedQualificationLevel(widget: PlatformWidget, organization: OrganizationDetailData): boolean {
    return <boolean>(
      (!widget.allowedQualificationLevels ||
        (organization.qualificationLevel &&
          widget.allowedQualificationLevels.includes(organization.qualificationLevel)))
    );
  }

  private updateAllWidgets(): void {
    this.allWidgets = this.getVisibleWidgets();
  }

  private getVisibleWidgets(): PlatformWidget[] {
    const guideWidgets = this.filterWidgetsById('guided-tour');
    const adminAreaWidget = this.filterWidgetsById('admin-widget');
    const serviceCatalogWidget = this.filterWidgetsById('service-catalog-widget');
    const blockedServiceWidgets = this.filterWidgetsById('blocked-service-widget');
    const reassignServiceRolesWidgets = this.verifyCartridgeId(this.filterWidgetsById('reassign-service-role'));

    return [
      ...this.staticWidgets,
      ...guideWidgets,
      ...adminAreaWidget,
      ...serviceCatalogWidget,
      ...blockedServiceWidgets,
      ...reassignServiceRolesWidgets,
    ];
  }

  private filterWidgetsById(idPattern: string): PlatformWidget[] {
    return this.visibleWidgets.filter(widget => widget.id.includes(idPattern)) as PlatformWidget[];
  }

  private verifyCartridgeId(platformWidgets: PlatformWidget[]): PlatformWidget[] {
    return platformWidgets.length > 0 && platformWidgets[0].cartridgeId ? platformWidgets : [];
  }
}
